import { makeAutoObservable, runInAction } from "mobx";
import { getClosedChatsStats, getOpenedChatsStats, getMyChatsStats } from "../http/chatAPI";

const DEFAULT_STATS = { unreadCount: 0, requestsCount: 0 };
const GLOBAL_KEY = "all";

export default class ChatStatsStore {
  constructor() {
    this._closedStatsByCategory = { [GLOBAL_KEY]: DEFAULT_STATS };
    this._openedStatsByCategory = { [GLOBAL_KEY]: DEFAULT_STATS };
    this._myStatsByCategory = { [GLOBAL_KEY]: DEFAULT_STATS };
    this._loadingByCategory = {};
    makeAutoObservable(this, {}, { autoBind: true });
  }

  _key(categoryId) {
    return categoryId || GLOBAL_KEY;
  }

  _normalizeStats(stats) {
    return {
      unreadCount: Number(stats?.unreadCount) || 0,
      requestsCount: Number(stats?.requestsCount) || 0,
    };
  }

  setClosedStats(categoryId, stats) {
    const key = this._key(categoryId);
    this._closedStatsByCategory[key] = this._normalizeStats(stats);
  }

  getClosedStats(categoryId = null) {
    const key = this._key(categoryId);
    return this._closedStatsByCategory[key] || DEFAULT_STATS;
  }

  setOpenedStats(categoryId, stats) {
    const key = this._key(categoryId);
    this._openedStatsByCategory[key] = this._normalizeStats(stats);
  }

  getOpenedStats(categoryId = null) {
    const key = this._key(categoryId);
    return this._openedStatsByCategory[key] || DEFAULT_STATS;
  }

  setMyStats(categoryId, stats) {
    const key = this._key(categoryId);
    this._myStatsByCategory[key] = this._normalizeStats(stats);
  }

  getMyStats(categoryId = null) {
    const key = this._key(categoryId);
    return this._myStatsByCategory[key] || DEFAULT_STATS;
  }

  isClosedStatsLoading(categoryId = null) {
    const key = this._key(categoryId);
    return !!this._loadingByCategory[key];
  }

  async loadClosedStats(adminId, categoryId = null) {
    if (!adminId) return this.getClosedStats(categoryId);

    const key = this._key(categoryId);
    runInAction(() => {
      this._loadingByCategory[key] = true;
    });

    try {
      const stats = await getClosedChatsStats(adminId, categoryId);
      runInAction(() => {
        this.setClosedStats(categoryId, stats);
      });
      return stats;
    } finally {
      runInAction(() => {
        this._loadingByCategory[key] = false;
      });
    }
  }

  async loadOpenedStats(adminId, categoryId = null) {
    if (!adminId) return this.getOpenedStats(categoryId);

    try {
      const stats = await getOpenedChatsStats(adminId, categoryId);
      runInAction(() => {
        this.setOpenedStats(categoryId, stats);
      });
      return stats;
    } catch (err) {
      console.error("Ошибка загрузки статистики открытых чатов:", err);
    }
  }

  async loadMyStats(adminId, categoryId = null) {
    if (!adminId) return this.getMyStats(categoryId);

    try {
      const stats = await getMyChatsStats(adminId, categoryId);
      runInAction(() => {
        this.setMyStats(categoryId, stats);
      });
      return stats;
    } catch (err) {
      console.error("Ошибка загрузки статистики моих чатов:", err);
    }
  }

  async handleSocketStatsUpdated(payload, adminId) {
    const categoryId = payload?.categoryId || null;

    // Новый формат: payload.closed и payload.opened
    const closedStats = payload?.closed || payload?.stats || DEFAULT_STATS;
    const openedStats = payload?.opened || null;

    this.setClosedStats(categoryId, closedStats);
    if (openedStats) {
      this.setOpenedStats(categoryId, openedStats);
    }

    // Если categoryId = null — глобальное обновление: обновляем все category-specific счётчики
    if (categoryId !== null || !adminId) return;

    const categoryIds = Object.keys(this._closedStatsByCategory).filter(
      (key) => key !== GLOBAL_KEY
    );

    await Promise.all([
      ...categoryIds.map((id) => this.loadClosedStats(adminId, id)),
      ...categoryIds.map((id) => this.loadOpenedStats(adminId, id)),
    ]);
  }

  reset() {
    this._closedStatsByCategory = { [GLOBAL_KEY]: DEFAULT_STATS };
    this._openedStatsByCategory = { [GLOBAL_KEY]: DEFAULT_STATS };
    this._myStatsByCategory = { [GLOBAL_KEY]: DEFAULT_STATS };
    this._loadingByCategory = {};
  }
}
